import { Unsubscribe } from 'router5/dist/types/base';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { IRouter } from '../../infrastructure/router';
import { RouteParams, Routes } from '../../routes-table';
import { IErrorParsing } from '../../infrastructure/error-parsing';
import { IModuleVM } from '../../infrastructure/components/module-vm';
import { IAlertsService } from '../../infrastructure/alerts';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../infrastructure/sub-windows';
import { IRolesDAO, IRolesDTO, IRolesListDTO } from '../../api/roles.dao';
import { Component } from '../../infrastructure/type-extensions';
import { RoleForm } from './_role-form/role-form';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../tracking-service/tracking-values';

class RolesVM implements IModuleVM {
    @mobx.observable
    public rolesList: IRolesDTO[];
    @mobx.observable
    public isLoading: boolean;
    @mobx.observable
    public isLoadingMore: boolean;
    @mobx.observable
    public roleIdToSubWindowMap: { [index: string]: ISubWindowFacade };
    private subscriptions: Unsubscribe[];
    @mobx.observable
    public loadMorePaginationToken: string | null;

    constructor(
        @IoC.Inject private readonly rolesDAO: IRolesDAO = IoC.Container.get(IRolesDAO),
        @IoC.Inject private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.roleIdToSubWindowMap = {};
    }

    didMount(): void {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listen(Routes.RolesList, async () => {
            this.fetchRolesList();
            this.trackingService.get().track(TRACKING_EVENTS.ROLES_PAGE_LOAD, {});
        }));
    }

    willUnmount(): void {
        this.subscriptions.forEach(x => x());
    }

    @mobx.action
    public fetchRolesList = async (needLoadMore = false) => {
        if (needLoadMore) {
            this.isLoadingMore = true;
        } else {
            this.loadMorePaginationToken = null;
            this.isLoading = true;
        }

        try {
            let params;
            if (this.loadMorePaginationToken) params = { nextPageToken: this.loadMorePaginationToken };
            const response = await this.rolesDAO.getRoles(params);
            this.onFetchRolesListSuccess(response, needLoadMore);
        } catch (error) {
            this.onFetchRolesListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
                this.isLoadingMore = false;
            });
        }
    };

    @mobx.action
    public fetchRolesPermissions = async id => {
        this.isLoading = true;
        try {
            const response = await this.rolesDAO.getPermissionsByRoleId(id);
            return response;
        } catch (error) {
            this.onFetchRolesListError(error);
        } finally {
            mobx.runInAction(() => {
                this.isLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchRolesListSuccess(response: IRolesListDTO, isLoadMore: boolean): void {
        if (isLoadMore) {
            this.rolesList.push(...response.roles || []);
        } else {
            this.rolesList = response.roles || [];
        }

        this.loadMorePaginationToken = response.nextPageToken;
    }

    @mobx.action
    private onFetchRolesListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }

    public showRoleForm = params => {
        const { title, linkName } = params;
        return this.subWindowService.showSubWindow({
            componentToHost: RoleForm,
            componentToHostProps: {
                fetchRolesList: this.fetchRolesList,
                closeEditRoleForm: this.closeEditRoleForm,
                linkName: linkName
            },
            title: title,
        });
    };

    public editRole = (linkName: string) => {
        let shallShowNewEditWindow = true;
        const alreadyShownSubWindow = this.roleIdToSubWindowMap[linkName + 'role'];

        if (alreadyShownSubWindow && alreadyShownSubWindow.getStatus() !== SubWindowStatus.Closed) {
            alreadyShownSubWindow.maximize();
            shallShowNewEditWindow = false;
        }

        if (shallShowNewEditWindow) {
            mobx.runInAction(() => {
                this.roleIdToSubWindowMap[linkName + 'role'] = this.showRoleForm({ title: 'Edit Role', linkName: linkName });
            });
        }
    };

    public closeEditRoleForm = (linkName: string) => {
        this.roleIdToSubWindowMap[linkName + 'role'].close();
    };

    public showCreateRoleForm = (intl: IntlShape) => {
        return this.subWindowService.showSubWindow({
            componentToHost: RoleForm,
            componentToHostProps: {
                fetchRolesList: this.fetchRolesList,
            },
            title: intl.formatMessage(
                {
                    id: `oe.role.newRole`,
                    description: 'New Role button',
                    defaultMessage: '{prefix}New Role',
                },
                {
                    prefix: ''
                }
            )
        });
    };

    @mobx.action
    public handlePermissionsClick = <T extends { id: string; roleName: string }>(
        name: string, roleId: string, HostComponent: Component<T>, isExpandedByDefault: boolean,
    ) => {
        const existingSubWindowForThisRole = this.roleIdToSubWindowMap[roleId + 'permission'];

        let shallOpenNewSubWindow = true;
        if (existingSubWindowForThisRole && existingSubWindowForThisRole.getStatus() !== SubWindowStatus.Closed) {
            existingSubWindowForThisRole.expand();
            shallOpenNewSubWindow = false;
        }

        if (shallOpenNewSubWindow) {

            this.roleIdToSubWindowMap[roleId + 'permission'] = this.subWindowService.showSubWindow({
                title: `Permission of ${name} Role`,
                isExpandedByDefault: isExpandedByDefault,
                componentToHost: HostComponent,
                componentToHostProps: {
                    id: roleId,
                    roleName: name
                },
            });
        }
    };

    public onLoadMore = async () => {
        this.fetchRolesList(true);
    };

    public isRoleFormShown = (roleId: string) => {
        return this.roleIdToSubWindowMap[roleId]
            && this.roleIdToSubWindowMap[roleId].getStatus() !== SubWindowStatus.Closed;
    };

    public closeSubWindow = (roleId: number) => {
        if (roleId) {
            this.roleIdToSubWindowMap[roleId].close();
        }
    };
}

export { RolesVM };
